import jsPDF from 'jspdf';
import dayjs from 'dayjs';

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return [85, 85, 85];
  const h = hex.replace('#', '');
  if (h.length < 6) return [85, 85, 85];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return [85, 85, 85];
  return [r, g, b];
};

const textOnColor = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.55 ? '#111111' : '#ffffff';
};

const slugify = (value) => (value || 'torneo')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40) || 'torneo';

const horarioDeGrupo = (grupo) => {
  const primera = grupo.parejas?.[0];
  if (primera?.diaSemana != null && primera?.franjaId != null) {
    return { diaSemana: primera.diaSemana, franjaId: primera.franjaId };
  }
  return {
    diaSemana: grupo.tgr_dia_semana ?? primera?.diaSemana ?? null,
    franjaId: grupo.tgr_franja_id ?? primera?.franjaId ?? null,
  };
};

const etiquetaFranja = (franja) => {
  if (!franja) return '';
  if (franja.horaDesde && franja.horaHasta) return `${franja.nombre} (${franja.horaDesde}-${franja.horaHasta})`;
  if (!franja.horaDesde && franja.horaHasta) return `${franja.nombre} (hasta ${franja.horaHasta})`;
  if (franja.horaDesde && !franja.horaHasta) return `${franja.nombre} (después ${franja.horaDesde})`;
  return franja.nombre;
};

const CHIP_H = 5.4;
const PAIR_LINE_H = 4.1;

const chipWidth = (doc, text) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  return doc.getTextWidth(text) + 5.2;
};

const drawChip = (doc, x, y, text, colorHex) => {
  const w = chipWidth(doc, text);
  const [r, g, b] = hexToRgb(colorHex);
  doc.setFillColor(r, g, b);
  doc.roundedRect(x, y, w, CHIP_H, 1.4, 1.4, 'F');
  const [fr, fg, fb] = hexToRgb(textOnColor(colorHex));
  doc.setTextColor(fr, fg, fb);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(text, x + 2.6, y + 3.7);
  return w;
};

const drawChipsRow = (doc, x, y, maxWidth, chips) => {
  let cx = x;
  let cy = y;
  let rowH = 0;
  chips.forEach((chip) => {
    if (!chip?.text) return;
    const w = chipWidth(doc, chip.text);
    if (cx > x && cx + w > x + maxWidth) {
      cx = x;
      cy += CHIP_H + 1.6;
    }
    drawChip(doc, cx, cy, chip.text, chip.color);
    cx += w + 2;
    rowH = cy + CHIP_H - y;
  });
  return rowH || 0;
};

const measureChipsHeight = (doc, maxWidth, chips) => {
  let cx = 0;
  let rows = 0;
  chips.forEach((chip) => {
    if (!chip?.text) return;
    const w = chipWidth(doc, chip.text);
    if (rows === 0) rows = 1;
    if (cx > 0 && cx + w > maxWidth) {
      rows += 1;
      cx = 0;
    }
    cx += w + 2;
  });
  if (!rows) return 0;
  return rows * CHIP_H + (rows - 1) * 1.6;
};

const chipsDeHorario = (dia, franja) => {
  const chips = [];
  if (dia) chips.push({ text: dia.nombre, color: dia.color || '#13c2c2' });
  if (franja) chips.push({ text: etiquetaFranja(franja), color: franja.color || '#a0d911' });
  return chips;
};

const measureParejaHeight = (doc, pareja, innerWidth) => {
  const textW = innerWidth - 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const lines = doc.splitTextToSize(pareja.etiqueta || '-', textW);
  const chipsH = measureChipsHeight(doc, textW, chipsDeHorario(pareja.dia, pareja.franja));
  const pendienteH = pareja.pendiente ? 4.2 : 0;
  return 5.4 + lines.length * PAIR_LINE_H + (chipsH ? chipsH + 2 : 0) + pendienteH;
};

const measureGrupoHeight = (doc, grupo, colWidth) => {
  const inner = colWidth - 10;
  let h = 6 + 7;
  if (grupo.semilla != null) h += 4;
  const headerChipsH = measureChipsHeight(doc, inner - 18, chipsDeHorario(grupo.dia, grupo.franja));
  if (headerChipsH) h += headerChipsH + 2;
  h += 3;
  if (!grupo.parejas.length) {
    h += 10;
  } else {
    grupo.parejas.forEach((p, i) => {
      h += measureParejaHeight(doc, p, inner);
      if (i < grupo.parejas.length - 1) h += 2.2;
    });
  }
  return h + 6;
};

const drawPareja = (doc, x, y, width, pareja) => {
  const h = measureParejaHeight(doc, pareja, width);
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, width, h, 1.6, 1.6, 'FD');

  const textW = width - 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(33, 33, 33);
  const lines = doc.splitTextToSize(pareja.etiqueta || '-', textW);
  doc.text(lines, x + 3, y + 5.6);

  let cy = y + 5.6 + lines.length * PAIR_LINE_H;
  const chipsH = drawChipsRow(doc, x + 3, cy, textW, chipsDeHorario(pareja.dia, pareja.franja));
  if (chipsH) cy += chipsH + 1.4;
  if (pareja.pendiente) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(210, 105, 30);
    doc.text('Pendiente de pago', x + 3, cy + 3.4);
  }
  return h;
};

const drawGrupo = (doc, x, y, width, height, grupo) => {
  doc.setFillColor(248, 249, 250);
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.setLineDashPattern([1.6, 1.2], 0);
  doc.roundedRect(x, y, width, height, 2.2, 2.2, 'FD');
  doc.setLineDashPattern([], 0);

  const innerX = x + 5;
  const innerW = width - 10;
  let cy = y + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text(`Grupo ${grupo.nombre}`, innerX, cy + 4);

  if (grupo.cupo != null) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    const cupoTxt = `${grupo.parejas.length}/${grupo.cupo}`;
    doc.text(cupoTxt, x + width - 5, cy + 4, { align: 'right' });
  }

  cy += 7;
  if (grupo.semilla != null) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Semilla ${grupo.semilla}`, innerX, cy + 3);
    cy += 4;
  }

  const headerChipsH = drawChipsRow(
    doc,
    innerX,
    cy,
    innerW - 16,
    chipsDeHorario(grupo.dia, grupo.franja)
  );
  if (headerChipsH) cy += headerChipsH + 3;
  else cy += 3;

  if (!grupo.parejas.length) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(140, 140, 140);
    doc.text('Sin parejas asignadas', innerX, cy + 4);
    return;
  }

  grupo.parejas.forEach((p) => {
    const ph = drawPareja(doc, innerX, cy, innerW, p);
    cy += ph + 2.2;
  });
};

const mapGrupos = (grupos, dias, franjas) => grupos.map((g) => {
  const horario = horarioDeGrupo(g);
  const dia = dias.find((d) => d.diaSemana === horario.diaSemana) || null;
  const franja = franjas.find((f) => f.id === horario.franjaId) || null;
  return {
    nombre: g.tgr_nombre,
    cupo: g.tgr_cupo,
    semilla: g.tgr_semilla ?? null,
    dia,
    franja,
    parejas: (g.parejas || []).map((p) => ({
      etiqueta: p.etiqueta,
      pendiente: p.tni_estado === 'PENDIENTE' || Boolean(p.cobro_pendiente),
      dia: dias.find((d) => d.diaSemana === p.diaSemana) || dia,
      franja: franjas.find((f) => f.id === p.franjaId) || franja,
    })),
  };
});

export const exportarGruposPdf = ({
  torneo,
  grupos = [],
  dias = [],
  franjas = [],
  gruposConfirmados = false,
}) => {
  const mapped = mapGrupos(
    [...grupos].sort((a, b) => (a.tgr_orden ?? 0) - (b.tgr_orden ?? 0)),
    dias,
    franjas
  );
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const gap = 7;
  const colW = (pageW - margin * 2 - gap) / 2;
  const footerReserve = 14;

  const nombreTorneo = torneo?.tor_nombre || 'Torneo';
  const fechaTorneo = torneo?.tor_fecha ? dayjs(torneo.tor_fecha).format('DD/MM/YYYY') : null;

  const drawHeader = (isFirstPage) => {
    let y = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isFirstPage ? 16 : 11);
    doc.setTextColor(20, 20, 20);
    doc.text(nombreTorneo, margin, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    const sub = isFirstPage ? 'Armado de grupos' : 'Armado de grupos (cont.)';
    doc.text(sub, margin, y + 11);

    const meta = [
      fechaTorneo ? `Fecha: ${fechaTorneo}` : null,
      gruposConfirmados ? 'Estado: confirmados' : 'Estado: borrador',
    ].filter(Boolean).join('   ·   ');
    doc.setFontSize(8.5);
    doc.text(meta, pageW - margin, y + 5, { align: 'right' });

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 14, pageW - margin, y + 14);
    return y + 18;
  };

  let y = drawHeader(true);
  let i = 0;

  while (i < mapped.length) {
    const g1 = mapped[i];
    const g2 = mapped[i + 1];
    const h1 = measureGrupoHeight(doc, g1, colW);
    const h2 = g2 ? measureGrupoHeight(doc, g2, colW) : 0;
    const rowH = Math.max(h1, h2);

    if (y + rowH > pageH - footerReserve) {
      doc.addPage();
      y = drawHeader(false);
    }

    drawGrupo(doc, margin, y, colW, rowH, g1);
    if (g2) {
      drawGrupo(doc, margin + colW + gap, y, colW, rowH, g2);
    }

    y += rowH + gap;
    i += g2 ? 2 : 1;
  }

  const pageCount = doc.getNumberOfPages();
  const generado = dayjs().format('DD/MM/YYYY HH:mm');
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`Generado ${generado}`, margin, pageH - 8);
    doc.text(`Página ${p} de ${pageCount}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  const fechaArchivo = dayjs().format('YYYY-MM-DD');
  doc.save(`grupos-${slugify(nombreTorneo)}-${fechaArchivo}.pdf`);
};
